import {
  Scene,
  screenProfileMap,
  audioProfileMap,
  UserRole,
  videoProfileMap,
  CoreError,
  PlayOption,
  RemoteStreamType,
  PlayoutDelay,
  JitterBufferDelay,
  SMALL_MODE,
  UpdateAudioProcessorOptions,
  InitAudioProcessorOptions,
  DeviceType
} from 'trtc-js-sdk-core';

import { IPluginClass } from 'trtc-js-sdk-core/src/plugin';

import type { CDNStreamingOptions } from '../dist/npm-package/plugins/cdn-streaming';
import type { DeviceDetectorOptions } from '../dist/npm-package/plugins/device-detector';
import type { VirtualBackgroundOptions, UpdateVirtualBackgroundOptions } from '../dist/npm-package/plugins/video-effect/virtual-background';
import type { WatermarkOptions } from '../dist/npm-package/plugins/video-effect/watermark';
import type { BeautyOptions, UpdateBeautyOptions } from '../dist/npm-package/plugins/video-effect/beauty';
import type { BasicBeautyOptions } from '../dist/npm-package/plugins/video-effect/basic-beauty';
import type { StartCrossRoomOption, UpdateCrossRoomOption, StopCrossRoomOption } from '../dist/npm-package/plugins/cross-room';
import { DecodeContextOption } from 'trtc-js-sdk-core/src/manager/video-decoder';
import { StartOption as AudioDecoderStartOption } from './plugins/audio-decoder';
import type { EncryptionOptions } from '../dist/npm-package/plugins/custom-encryption';
import type { SmallStreamAutoSwitcherOptions } from '../dist/npm-package/plugins/small-stream-auto-switcher/small-stream-auto-switcher.esm.d';
import type { StartChorusOption, UpdateChorusOption } from '@rtc-plugin/chorus';

export type PluginWithAssets = {
  plugin: IPluginClass;
  assetsPath?: string;
};

export interface TRTCOptions {
  plugins?: Array<IPluginClass>;
  enableSEI?: boolean;
  assetsPath?: string;
  videoDecodeFallback?: 'auto' | 'wasm' | 'wasm_video' | 'wasm_2d';
  volumeType?: TRTCVolumeType;
  enableAutoSwitchWhenRecapturing?: boolean;
  enableChorus?: boolean; // 开启合唱模式
  enableDbVolume?: boolean;
  enableVolumeControlInIOS?: boolean;
}
export interface LocalVideoConfig {
  view?: string | HTMLElement | HTMLElement[] | null;
  capture?: boolean,
  publish?: boolean;
  forcePublish?: boolean;
  mute?: boolean | string;
  option?: {
    cameraId?: string;
    useFrontCamera?: boolean;
    profile?: keyof typeof videoProfileMap | VideoProfile;
    fillMode?: 'contain' | 'cover' | 'fill';
    mirror?: boolean | string;
    small?: keyof typeof videoProfileMap | VideoProfile | boolean;
    smallMode?: SMALL_MODE;
    qosPreference?: typeof TRTCType.QOS_PREFERENCE_SMOOTH | typeof TRTCType.QOS_PREFERENCE_CLEAR;
    videoTrack?: MediaStreamTrack;
    avoidCropping?: boolean;
    retryWhenExactFailed?: boolean;
    rotation?: 0 | 90 | 180 | 270;
  };
}

export interface ProxyServer {
  websocketProxy?: string;
  loggerProxy?: string;
  turnServer?: TurnServerOptions | TurnServerOptions[];
  iceTransportPolicy?: RTCIceTransportPolicy;
  webtransportProxy?: string;
  scheduleProxy?: string;
  unifiedProxy?: string;
}

export interface SwitchRoomConfig {
  roomId?: number,
  strRoomId?: string;
  privateMapKey?: string;
  userSig: string;
}

export interface QuickStartConfig {
  remoteUserId: string;
  small?: boolean;
  domain?: string;
}

export interface EnterRoomConfig {
  sdkAppId: number;
  userId: string;
  userSig: string;
  roomId?: number;
  strRoomId?: string;
  role?: UserRole;
  autoReceiveAudio?: boolean;
  autoReceiveVideo?: boolean;
  privateMapKey?: string;
  latencyLevel?: number;
  businessInfo?: string;
  enableAutoPlayDialog?: boolean; // 默认为true
  proxy?: ProxyServer | string;
  scene?: Scene;
  userDefineRecordId?: string;

  priority?: RTCPriorityType;
  playoutDelay?: PlayoutDelay;
  jitterBufferDelay?: JitterBufferDelay;
  quickStart?: QuickStartConfig;
}

export interface ScreenShareConfig {
  view?: string | HTMLElement | HTMLElement[] | null;
  publish?: boolean;
  streamType?: TRTCStreamType;
  muteSystemAudio?: boolean;
  option?: {
    profile?: keyof typeof screenProfileMap | VideoProfile;
    fillMode?: 'contain' | 'cover' | 'fill',
    systemAudio?: boolean;
    echoCancellation?: boolean;
    autoGainControl?: boolean;
    noiseSuppression?: boolean;
    audioTrack?: MediaStreamTrack;
    videoTrack?: MediaStreamTrack;
    captureElement?: HTMLElement;
    preferDisplaySurface?: 'current-tab' | 'tab' | 'window' | 'monitor';
    qosPreference?: typeof TRTCType.QOS_PREFERENCE_SMOOTH | typeof TRTCType.QOS_PREFERENCE_CLEAR;
    selfBrowserSurface?: 'include' | 'exclude';
  };
}

export interface UpdateScreenShareConfig extends ScreenShareConfig {
  option?: {
    fillMode?: 'contain' | 'cover' | 'fill',
    qosPreference?: typeof TRTCType.QOS_PREFERENCE_SMOOTH | typeof TRTCType.QOS_PREFERENCE_CLEAR;
    audioTrack?: MediaStreamTrack;
    videoTrack?: MediaStreamTrack;
  };
}

export interface RemoteVideoConfig {
  view?: string | HTMLElement | HTMLElement[] | null;
  userId: string;
  streamType: TRTCStreamType;
  mute?: boolean;
  option?: {
    fillMode?: 'contain' | 'cover' | 'fill';
    small?: boolean; // 是否拉小流
    receiveWhenViewVisible?: boolean; // 是否开启【只拉可视区域视频流】功能
    viewRoot?: HTMLElement;
    draggable?: boolean;
    pictureInPicture?: boolean;
    fullScreen?: boolean;
  } & PlayOption;
}

export interface StopRemoteVideoConfig {
  userId: string;
  streamType?: TRTCStreamType;
}


export interface LocalAudioConfig {
  publish?: boolean;
  mute?: boolean | 'microphone';
  muteKeepVolumeDetection?: boolean;
  option?: {
    microphoneId?: string;
    profile?: keyof typeof audioProfileMap;
    audioTrack?: MediaStreamTrack;
    captureVolume?: number;
    earMonitorVolume?: number;

    echoCancellation?: boolean | string;
    autoGainControl?: boolean;
    noiseSuppression?: boolean;
  };
}

export interface UpdateLocalAudioConfig extends LocalAudioConfig {
  option?: {
    microphoneId?: string;
    audioTrack?: MediaStreamTrack;
    captureVolume?: number;
    earMonitorVolume?: number;
    echoCancellation?: boolean | string;
    autoGainControl?: boolean;
    noiseSuppression?: boolean;
  };
}

export interface RemoteAudioConfig {
  userId: string;
}

export interface StopRemoteAudioConfig {
  userId: string;
}

export const enum TRTCStreamType {
  Main = 'main',
  Sub = 'sub'
}


export enum TRTCDeviceType {
  Camera = 'camera',
  Microphone = 'microphone',
  Speaker = 'speaker'
}

export enum TRTCDeviceAction {
  Remove = 'remove',
  Add = 'add',
  Active = 'active'
}

export const enum TRTCVolumeType {
  DEFAULT,
  DB
}

export interface RTCErrorParams {
  code: number,
  extraCode?: number,
  functionName?: string,
  fnName?: string,
  message?: string,
  messageParams?: any,
  originError?: Error | DOMException;
  data?: any;
}

export interface RTCErrorInterface {
  readonly name: string,
  readonly code: number,
  readonly extraCode?: number,
  readonly functionName?: string,
  readonly message?: string,
  readonly originError?: Error | DOMException;
}

// export interface ErrorInfoParams {
// export interface InvalidParameterErrorMsgParams {
export interface ErrorMessageParams {
  key?: string,
  rule?: any,
  fnName?: string,
  fnParams?: any,
  value?: string | number | any;
  type?: string,
  deviceType?: string,
  error?: Error | DOMException | CoreError;
}

/**
 * **TRTC Constants**<br>
 * @module TYPE
 * @example
 * // Usage:
 * TRTC.TYPE.SCENE_LIVE
 */
export const TRTCType = {
  /**
     * Live streaming scene
     * @default 'live'
     * @memberof module:TYPE
     */
  SCENE_LIVE: Scene.LIVE,
  /**
     * RTC scene
     * @default 'rtc'
     * @memberof module:TYPE
    */
  SCENE_RTC: Scene.RTC,
  /**
     * Anchor role
     * @default 'anchor'
     * @memberof module:TYPE
     */
  ROLE_ANCHOR: UserRole.ANCHOR,
  /**
     * Audience role
     * @default 'audience'
     * @memberof module:TYPE
     */
  ROLE_AUDIENCE: UserRole.AUDIENCE,
  /**
     * Main stream
     *
     * - TRTC has a main video stream (main stream) and an sub video stream (sub stream)
     * - The camera is published through the main stream, and the screen sharing is published through the sub stream.
     * - The main video stream includes: high-definition large picture and low-definition small picture. By default, {@link TRTC#startRemoteVideo TRTC.startRemoteVideo} plays the high-definition large picture, and the low-definition small picture can be played through the small parameter. Refer to: [Enable small stream function](./tutorial-27-advanced-small-stream.html).
     * @default 'main'
     * @memberof module:TYPE
     */
  STREAM_TYPE_MAIN: TRTCStreamType.Main,
  /**
     * Sub stream
     * @default 'sub'
     * @memberof module:TYPE
     */

  STREAM_TYPE_SUB: TRTCStreamType.Sub,
  /**
     * Standard audio quality
    * | Audio Profile | Sampling Rate | Channel | Bitrate (kbps) |
    * | :---        | :---  | :--- | :---       |
    * | TRTC.TYPE.AUDIO_PROFILE_STANDARD    | 48000 | Mono| 40         |
    * | TRTC.TYPE.AUDIO_PROFILE_HIGH        | 48000 | Mono| 128        |
    * | TRTC.TYPE.AUDIO_PROFILE_STANDARD_STEREO | 48000 | Stereo| 64        |
    * | TRTC.TYPE.AUDIO_PROFILE_HIGH_STEREO | 48000 | Stereo| 192        |
    * @default 'standard'
    * @memberof module:TYPE
    */
  AUDIO_PROFILE_STANDARD: 'standard',
  /**
     * Standard stereo audio quality
   * | Audio Profile | Sampling Rate | Channel | Bitrate (kbps) |
    * | :---        | :---  | :--- | :---       |
    * | TRTC.TYPE.AUDIO_PROFILE_STANDARD    | 48000 | Mono| 40         |
    * | TRTC.TYPE.AUDIO_PROFILE_HIGH        | 48000 | Mono| 128        |
    * | TRTC.TYPE.AUDIO_PROFILE_STANDARD_STEREO | 48000 | Stereo| 64        |
    * | TRTC.TYPE.AUDIO_PROFILE_HIGH_STEREO | 48000 | Stereo| 192        |
    * @default 'standard-stereo'
    * @memberof module:TYPE
    */
  AUDIO_PROFILE_STANDARD_STEREO: 'standard-stereo',
  /**
     * High audio quality
     * | Audio Profile | Sampling Rate | Channel | Bitrate (kbps) |
     * | :---        | :---  | :--- | :---       |
     * | TRTC.TYPE.AUDIO_PROFILE_STANDARD    | 48000 | Mono| 40         |
     * | TRTC.TYPE.AUDIO_PROFILE_HIGH        | 48000 | Mono| 128        |
     * | TRTC.TYPE.AUDIO_PROFILE_STANDARD_STEREO | 48000 | Stereo| 64        |
     * | TRTC.TYPE.AUDIO_PROFILE_HIGH_STEREO | 48000 | Stereo| 192        |
    * @default 'high'
    * @memberof module:TYPE
    */

  AUDIO_PROFILE_HIGH: 'high',
  /**
     * High-quality stereo audio
     * | Audio Profile | Sampling Rate | Channel | Bitrate (kbps) |
     * | :---        | :---  | :--- | :---       |
     * | TRTC.TYPE.AUDIO_PROFILE_STANDARD    | 48000 | Mono| 40         |
     * | TRTC.TYPE.AUDIO_PROFILE_HIGH        | 48000 | Mono| 128        |
     * | TRTC.TYPE.AUDIO_PROFILE_STANDARD_STEREO | 48000 | Stereo| 64        |
     * | TRTC.TYPE.AUDIO_PROFILE_HIGH_STEREO | 48000 | Stereo| 192        |
     * @default 'high-stereo'

    * @memberof module:TYPE
    */
  AUDIO_PROFILE_HIGH_STEREO: 'high-stereo',
  /**
   * When the network is weak, the video encoding strategy takes 'smooth' first, i.e., the priority is to preserve frame rate.
   * <br>
   * Default 'smooth' first for camera, 'default' clear first for screen sharing
  * @default 'smooth'
  * @memberof module:TYPE
  */
  QOS_PREFERENCE_SMOOTH: 'smooth',
  /**
   * When the network is weak, the video encoding strategy takes 'clear' first, i.e., the priority is to preserve resolution.
   * <br>
   * Default 'smooth' first for camera, 'default' clear first for screen sharing
  * @default 'clear'
  * @memberof module:TYPE
  */
  QOS_PREFERENCE_CLEAR: 'clear',
  /**
   * Audio output device types in Android. Specify this device when capture microphone, and the sound will come out of the speaker.
  */
  SPEAKER: 'Speakerphone',
  /**
   * Audio output device types in Android. Specify this device when capture microphone, and the sound will come out of the headset.
  */
  HEADSET: 'Headset earpiece'
} as const;


export interface AudioMixerOptions {
  id: string;
  url: string;
  loop?: boolean;
  volume?: number;
}

export interface UpdateAudioMixerOptions {
  id: string;
  loop?: boolean;
  volume?: number;
  seekFrom?: number;
  operation?: 'pause' | 'resume' | 'stop';
}

export interface StopAudioMixerOptions {
  id: string;
}

export interface AIDenoiserOptions {
  assetsPath: string;
  sdkAppId: number;
  userId: string;
  userSig: string;
}

export interface RealtimeTranscriberStartOptions {
  /**
   * Source language code, currently only supports 'zh' and 'en'.
   */
  sourceLanguage: 'zh' | 'en';
  /**
   * Target translation languages, single language or array, e.g. 'en' or ['en', 'ja'].
   */
  translationLanguages?: string | string[];
  /**
   * User IDs to transcribe, default is 'all' (transcribe everyone), or specify one/many user IDs.
   */
  userIdsToTranscribe?: 'all' | string | string[];
  /**
   * Custom transcriber robot ID. If omitted, the SDK generates one automatically.
   */
  transcriberRobotId?: string;
}

export interface RealtimeTranscriberStopOptions {
  /**
   * The transcriber robot ID returned by startPlugin.
   */
  transcriberRobotId: string;
}

// 对函数类型推导生效，不用于 jsdoc
export type PluginStartOptionsMap = {
  'AudioMixer': AudioMixerOptions;
  'AIDenoiser': AIDenoiserOptions;
  'CDNStreaming': CDNStreamingOptions;
  'VirtualBackground': VirtualBackgroundOptions;
  'Watermark': WatermarkOptions;
  'Beauty': BeautyOptions;
  'BasicBeauty': BasicBeautyOptions;
  'DeviceDetector': DeviceDetectorOptions;
  'SEI': undefined;
  'Debug': undefined;
  'CrossRoom': StartCrossRoomOption;
  'TRTCVideoDecoder': Omit<DecodeContextOption, 'createDecoder'>;
  'TRTCAudioDecoder': AudioDecoderStartOption;
  'CustomEncryption': EncryptionOptions;
  'SmallStreamAutoSwitcher': SmallStreamAutoSwitcherOptions;
  'AudioProcessor': InitAudioProcessorOptions;
  'Chorus': StartChorusOption;
  'RealtimeTranscriber': RealtimeTranscriberStartOptions;
};

export type PluginUpdateOptionsMap = {
  'AudioMixer': UpdateAudioMixerOptions;
  'CDNStreaming': CDNStreamingOptions;
  'VirtualBackground': UpdateVirtualBackgroundOptions;
  'Watermark': WatermarkOptions;
  'Beauty': UpdateBeautyOptions;
  'BasicBeauty': BasicBeautyOptions;
  'SEI': UpdateSEIOptions;
  'CrossRoom': UpdateCrossRoomOption;
  'AudioProcessor': UpdateAudioProcessorOptions;
  'Chorus': UpdateChorusOption;
};

export interface UpdateSEIOptions {
  buffer: ArrayBuffer,
  options: { seiPayloadType: number; toSubStream?: boolean, small?: boolean; };
}

export type PluginStopOptionsMap = {
  'AudioMixer': StopAudioMixerOptions;
  'AIDenoiser': undefined;
  'CDNStreaming': CDNStreamingOptions;
  'VirtualBackground': undefined;
  'Watermark': undefined;
  'Beauty': undefined;
  'BasicBeauty': undefined;
  'DeviceDetector': undefined;
  'Debug': undefined;
  'CrossRoom': StopCrossRoomOption | undefined;
  'SmallStreamAutoSwitcher': undefined;
  'AudioProcessor': undefined;
  'CustomEncryption': undefined;
  'Chorus': undefined;
  'RealtimeTranscriber': RealtimeTranscriberStopOptions;
};


export interface TRTCStatistics {
  rtt: number;
  downLoss: number;
  upLoss: number;
  bytesSent: number;
  bytesReceived: number;
  localStatistics: LocalStatistic;
  remoteStatistics: RemoteStatistic[];
}

interface LocalStatistic {
  audio: {
    bitrate: number;
    audioLevel: number;
  },
  video: {
    width: number;
    height: number;
    frameRate: number;
    bitrate: number;
    videoType: TRTCVideoType;
  }[];
}

export enum TRTCVideoType {
  Big = 'big',
  Small = 'small',
  Sub = 'sub'
}

interface RemoteStatistic {
  audio: {
    bitrate: number;
    audioLevel: number;
  },
  video: {
    width: number;
    height: number;
    frameRate: number;
    bitrate: number;
    videoType: TRTCVideoType;
  }[],
  userId: string;
}

export interface VideoFrameConfig {
  userId?: string;
  streamType?: TRTCStreamType;
}

export interface FirstVideoFrameInfo {
  width: number;
  height: number;
  streamType: RemoteStreamType;
  userId: string;
}

export interface PermissionOption {
  request?: boolean;
  types?: DeviceType[];
}

export interface PermissionResult {
  camera: PermissionState | null;
  microphone: PermissionState | null;
}

export interface TranslationText {
  language: string;
  text: string;
}

export interface RealtimeTranscriberMessage {
  segmentId: string;
  speakerUserId: string;
  sourceText: string;
  translationTexts: TranslationText[];
  timestamp: number;
  isCompleted: boolean;
  robotId: string;
}
export interface PictureInPictureStateChangedEvent {
    userId: string;
    streamType: TRTCStreamType;
    isPictureInPicture: boolean;
    /**
     * isPictureInPicture 为 true 时存在。
     * 包含 PiP 窗口对象，用户可用于监听 resize
     */
    pictureInPictureWindow?: PictureInPictureWindow;
}

export interface FullScreenStateChangedEvent {
    userId: string;
    streamType: TRTCStreamType;
    isFullScreen: boolean;
}
